"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import styles from "./index-fluctuation.module.css";

type Point = {
  time: string;
  value: number | null;
  volume?: number | null;
};

type IndexSeries = {
  key: string;
  label: string;
  first: number | null;
  latest: number | null;
  change: number | null;
  change_pct: number | null;
  high: number | null;
  low: number | null;
  points: Point[];
};

type HistoricalRangePayload = {
  kind: string;
  available: boolean;
  series: IndexSeries[];
};

type IndexFluctuationPayload = {
  ok: boolean;
  source_url: string;
  as_of_time: string | null;
  range: { start: string; end: string };
  sample_count: number;
  series: IndexSeries[];
  historical_ranges?: Record<string, HistoricalRangePayload>;
};

const RANGE_TABS = ["1D", "1M", "3M", "6M", "1Y"] as const;
const CHART_WIDTH = 1180;
const CHART_HEIGHT = 340;
const CHART_PAD_LEFT = 8;
const CHART_PAD_RIGHT = 78;
const CHART_PAD_TOP = 18;
const CHART_PAD_BOTTOM = 28;
const PLOT_WIDTH = CHART_WIDTH - CHART_PAD_LEFT - CHART_PAD_RIGHT;
const PLOT_HEIGHT = CHART_HEIGHT - CHART_PAD_TOP - CHART_PAD_BOTTOM;

const fmt = (value: number | null | undefined, digits = 2) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return value.toLocaleString(undefined, { maximumFractionDigits: digits });
};

const fmtSigned = (value: number | null | undefined, digits = 2) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return `${value > 0 ? "+" : ""}${fmt(value, digits)}`;
};

const fmtPct = (value: number | null | undefined) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return `${value > 0 ? "+" : ""}${fmt(value, 2)}%`;
};

const valueTone = (value: number | null | undefined) => {
  if (typeof value !== "number" || Number.isNaN(value) || value === 0) return styles.neutral;
  return value > 0 ? styles.positive : styles.negative;
};

const numericPoints = (points: Point[]) =>
  points.filter((point): point is Point & { value: number } => typeof point.value === "number" && !Number.isNaN(point.value));

function makeChartModel(series: IndexSeries) {
  const points = numericPoints(series.points);
  const values = points.map((point) => point.value);
  if (points.length < 2 || values.length < 2) {
    return { linePath: "", areaPath: "", yTicks: [], xTicks: [], hasVolume: false };
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const padding = Math.max((max - min) * 0.12, max * 0.001);
  const low = min - padding;
  const high = max + padding;
  const span = high - low || 1;
  const yFor = (value: number) => CHART_PAD_TOP + PLOT_HEIGHT - ((value - low) / span) * PLOT_HEIGHT;
  const xFor = (index: number) => CHART_PAD_LEFT + (index / Math.max(1, points.length - 1)) * PLOT_WIDTH;

  const coords = points.map((point, index) => ({
    x: xFor(index),
    y: yFor(point.value),
    time: point.time,
    value: point.value,
  }));

  const linePath = coords.map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(2)},${point.y.toFixed(2)}`).join(" ");
  const areaPath = `${linePath} L${coords[coords.length - 1].x.toFixed(2)},${CHART_PAD_TOP + PLOT_HEIGHT} L${coords[0].x.toFixed(2)},${CHART_PAD_TOP + PLOT_HEIGHT} Z`;
  const yTicks = Array.from({ length: 5 }, (_, index) => {
    const value = low + (span * index) / 4;
    return { value, y: yFor(value) };
  }).reverse();
  const xTickIndexes = [0, Math.floor(points.length * 0.25), Math.floor(points.length * 0.5), Math.floor(points.length * 0.75), points.length - 1];
  const xTicks = xTickIndexes.map((index) => ({ x: xFor(index), label: points[index]?.time ?? "" }));
  const hasVolume = points.some((point) => typeof point.volume === "number" && point.volume > 0);

  return { linePath, areaPath, yTicks, xTicks, hasVolume };
}

function AreaChart({ series, activeRange }: { series: IndexSeries; activeRange: string }) {
  const model = useMemo(() => makeChartModel(series), [series]);

  return (
    <div className={styles.chartFrame}>
      <svg className={styles.mainChart} viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`} role="img" aria-label={`${series.label} ${activeRange} index chart`}>
        <defs>
          <linearGradient id="index-area-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#f2b844" stopOpacity="0.34" />
            <stop offset="100%" stopColor="#f2b844" stopOpacity="0.12" />
          </linearGradient>
        </defs>

        <rect x={CHART_PAD_LEFT} y={CHART_PAD_TOP} width={PLOT_WIDTH} height={PLOT_HEIGHT} className={styles.plotBg} />

        {model.yTicks.map((tick) => (
          <g key={tick.y}>
            <line x1={CHART_PAD_LEFT} x2={CHART_PAD_LEFT + PLOT_WIDTH} y1={tick.y} y2={tick.y} className={styles.gridLine} />
            <text x={CHART_PAD_LEFT + PLOT_WIDTH + 12} y={tick.y + 4} className={styles.axisText}>
              {fmt(tick.value, 0)}
            </text>
          </g>
        ))}

        {model.xTicks.map((tick) => (
          <g key={`${tick.x}-${tick.label}`}>
            <line x1={tick.x} x2={tick.x} y1={CHART_PAD_TOP} y2={CHART_PAD_TOP + PLOT_HEIGHT} className={styles.gridLine} />
            <text x={tick.x} y={CHART_HEIGHT - 7} className={styles.xAxisText}>
              {tick.label}
            </text>
          </g>
        ))}

        {model.areaPath ? <path d={model.areaPath} className={styles.areaFill} /> : null}
        {model.linePath ? <path d={model.linePath} className={styles.areaLine} /> : null}
      </svg>

      {activeRange === "1D" && !model.hasVolume ? (
        <p className={styles.volumeFootnote}>Volume is not included in the PSX index fluctuation file; this view is plotting index level only.</p>
      ) : activeRange !== "1D" ? (
        <p className={styles.volumeFootnote}>Historical ranges use PSX daily index workbooks normalized to the latest official intraday level.</p>
      ) : null}
    </div>
  );
}

export default function IndexFluctuationClient() {
  const [data, setData] = useState<IndexFluctuationPayload | null>(null);
  const [selectedKey, setSelectedKey] = useState("kse_100");
  const [activeRange, setActiveRange] = useState<(typeof RANGE_TABS)[number]>("1D");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load(mode: "initial" | "refresh" = "initial") {
    try {
      if (mode === "initial") setLoading(true);
      if (mode === "refresh") setRefreshing(true);
      setError(null);

      const response = await fetch("/api/indices/fluctuation", { cache: "no-store" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = (await response.json()) as IndexFluctuationPayload;
      setData(payload);
      if (!payload.series.some((row) => row.key === selectedKey)) {
        setSelectedKey(payload.series[0]?.key ?? "kse_100");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load PSX index fluctuation data.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeSeriesList = activeRange === "1D" ? data?.series ?? [] : data?.historical_ranges?.[activeRange]?.series ?? [];
  const selectedSeries = activeSeriesList.find((row) => row.key === selectedKey) ?? activeSeriesList[0] ?? null;
  const activePointCount = selectedSeries?.points.length ?? 0;
  const rangeLabel = activeRange === "1D" ? `${data?.range.start ?? ""} to ${data?.range.end ?? ""}` : activeRange;

  return (
    <main className={styles.page}>
      <section className={styles.shell}>
        <div className={styles.headerRow}>
          <div>
            <p className={styles.eyebrow}>Official PSX intraday feed</p>
            <h1>Index Performance</h1>
          </div>
          <button type="button" onClick={() => load("refresh")} disabled={refreshing} className={styles.refreshButton}>
            <RefreshCw size={16} />
            {refreshing ? "Refreshing" : "Refresh"}
          </button>
        </div>

        {error ? (
          <section className={styles.errorBox}>
            <strong>Could not load PSX feed</strong>
            <p>{error}</p>
            <button type="button" onClick={() => load("refresh")}>Try again</button>
          </section>
        ) : null}

        {loading ? (
          <section className={styles.loading}>Loading PSX index performance...</section>
        ) : data && selectedSeries ? (
          <>
            <div className={styles.summaryRow}>
              <div>
                <span>{selectedSeries.label}</span>
                <strong>{fmt(selectedSeries.latest)}</strong>
              </div>
              <em className={valueTone(selectedSeries.change)}>
                {fmtSigned(selectedSeries.change)} pts ({fmtPct(selectedSeries.change_pct)})
              </em>
            </div>

            <div className={styles.indexTabs} aria-label="Select index">
              {data.series.map((row) => (
                <button
                  key={row.key}
                  type="button"
                  className={row.key === selectedSeries.key ? styles.activeIndexTab : undefined}
                  onClick={() => setSelectedKey(row.key)}
                >
                  {row.label}
                </button>
              ))}
            </div>

            <div className={styles.chartCard}>
              <div className={styles.rangeTabs} aria-label="Select range">
                {RANGE_TABS.map((range) => (
                  <button
                    key={range}
                    type="button"
                    className={range === activeRange ? styles.activeRangeTab : undefined}
                    disabled={range !== "1D" && !data.historical_ranges?.[range]?.available}
                    onClick={() => setActiveRange(range)}
                    title={range === "1D" ? "One day" : `${range} history`}
                  >
                    {range}
                  </button>
                ))}
              </div>

              <AreaChart series={selectedSeries} activeRange={activeRange} />
            </div>

            <section className={styles.statStrip} aria-label={`${selectedSeries.label} latest snapshot`}>
              <div>
                <span>Open</span>
                <strong>{fmt(selectedSeries.first)}</strong>
              </div>
              <div>
                <span>Low</span>
                <strong>{fmt(selectedSeries.low)}</strong>
              </div>
              <div>
                <span>High</span>
                <strong>{fmt(selectedSeries.high)}</strong>
              </div>
              <div>
                <span>Samples</span>
                <strong>{activePointCount.toLocaleString()}</strong>
              </div>
              <div>
                <span>As of</span>
                <strong>{activeRange === "1D" ? data.as_of_time || data.range.end : selectedSeries.points.at(-1)?.time ?? "-"}</strong>
              </div>
            </section>

            <section className={styles.sourceNote}>
              <span>{rangeLabel}</span>
              <a href={data.source_url} target="_blank" rel="noreferrer">
                Source: PSX {activeRange === "1D" ? "kse_index.lis.Z" : "historical index workbooks"}
              </a>
              <Link href="/indices/KSE-100">Open KSE-100 dashboard</Link>
            </section>
          </>
        ) : null}
      </section>
    </main>
  );
}
